import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CalendarClock,
  Calculator,
  CreditCard,
  HandCoins,
  Landmark,
  Scale,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricCard } from '@/components/financials/MetricCard';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  useBankAccounts,
  useChartOfAccounts,
  useMonthlyBalanceSheet,
} from '@/features/financials/hooks';
import type { Tables } from '@/types/database';
import { isoToLocalDate, todayISO } from './loans/loan-math';
import {
  FAMILY_LOAN_CONFIG,
  FAMILY_LOAN_SLUG,
  SBA_LOAN_CONFIG,
  SBA_LOAN_SLUG,
} from './loans/loan-configs';
import {
  loanRecordsStorageKey,
  readRecordsFromStorage,
  type PaymentRecords,
} from './loans/loan-records';
import {
  addDaysISO,
  rollupCreditAccount,
  rollupSbaLoan,
  rollupTermLoan,
  simulatePayoff,
  weightedAverageRate,
  type DebtKind,
  type DebtRow,
  type UpcomingPayment,
} from './loans/debt-rollup';

/** How far ahead the "Upcoming payments" card looks. */
const UPCOMING_HORIZON_DAYS = 90;

// Loan rates routinely carry two decimals (9.25%) so we can't use the
// app-wide `formatPercent` which clamps to one fractional digit.
const RATE_FMT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});
const formatRate = (rate: number | null) => (rate == null ? '—' : RATE_FMT.format(rate));
const formatISO = (iso: string | null) => (iso ? formatDate(isoToLocalDate(iso)) : '—');

const KIND_ICON: Record<DebtKind, LucideIcon> = {
  'term-loan': Users,
  'sba-loan': Landmark,
  'credit-card': CreditCard,
};

// Mercury surfaces credit lines as accounts with `account_type = 'credit'`
// — same heuristic the Accounts page uses.
function isCreditAccount(t: string | null | undefined): boolean {
  return (t ?? '').toLowerCase().includes('credit');
}

/**
 * Read the family-loan payment records the ledger page persists to
 * localStorage, and refresh if another tab marks a payment paid.
 */
function useLoanRecords(slug: string): PaymentRecords {
  const [records, setRecords] = useState<PaymentRecords>(() => readRecordsFromStorage(slug));
  useEffect(() => {
    const key = loanRecordsStorageKey(slug);
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === key) setRecords(readRecordsFromStorage(slug));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [slug]);
  return records;
}

export function DebtBalancesPage() {
  const today = todayISO();
  const horizon = addDaysISO(today, UPCOMING_HORIZON_DAYS);

  const familyRecords = useLoanRecords(FAMILY_LOAN_SLUG);
  const { data: accounts, isLoading: accountsLoading } = useBankAccounts();
  const { data: coa } = useChartOfAccounts();
  const { data: monthlyBalances } = useMonthlyBalanceSheet();

  const { rows, upcoming } = useMemo(() => {
    const family = rollupTermLoan(
      FAMILY_LOAN_CONFIG,
      FAMILY_LOAN_SLUG,
      familyRecords,
      '/financials/loans/family',
      { today, horizon },
    );
    const sba = rollupSbaLoan(SBA_LOAN_CONFIG, SBA_LOAN_SLUG, '/financials/loans/sba', {
      today,
      horizon,
    });
    const credit = (accounts ?? [])
      .filter((a) => isCreditAccount(a.account_type))
      .map((a) =>
        rollupCreditAccount({
          id: a.id,
          name: a.name,
          institution: a.institution,
          provider: a.provider,
          currentBalanceCents: a.current_balance_cents,
          availableBalanceCents: a.available_balance_cents,
        }),
      );

    const rows: DebtRow[] = [family.row, sba.row, ...credit].sort(
      (a, b) => b.balanceCents - a.balanceCents,
    );
    const upcoming: UpcomingPayment[] = [...family.upcoming, ...sba.upcoming].sort((a, b) =>
      a.dueDate === b.dueDate ? a.debtName.localeCompare(b.debtName) : a.dueDate < b.dueDate ? -1 : 1,
    );
    return { rows, upcoming };
  }, [familyRecords, accounts, today, horizon]);

  const totals = useMemo(() => {
    const balance = rows.reduce((s, r) => s + r.balanceCents, 0);
    const monthly = rows.reduce((s, r) => s + (r.monthlyPaymentCents ?? 0), 0);
    const monthlyUnknown = rows.filter(
      (r) => r.monthlyPaymentCents == null && r.balanceCents > 0,
    ).length;
    const interest = rows.reduce((s, r) => s + (r.interestRemainingCents ?? 0), 0);
    const available = rows.reduce((s, r) => s + (r.availableCents ?? 0), 0);
    const next = rows
      .filter((r) => r.nextDueDate && r.nextPaymentCents != null)
      .sort((a, b) => (a.nextDueDate! < b.nextDueDate! ? -1 : 1))[0];
    const overdue = upcoming.filter((u) => u.overdue);
    return {
      balance,
      monthly,
      monthlyUnknown,
      interest,
      available,
      next: next ?? null,
      avgRate: weightedAverageRate(rows),
      overdueCount: overdue.length,
      overdueCents: overdue.reduce((s, u) => s + u.amountCents, 0),
    };
  }, [rows, upcoming]);

  const bookLiabilities = useMemo(
    () => buildBookLiabilities(coa ?? [], monthlyBalances ?? [], accounts ?? []),
    [coa, monthlyBalances, accounts],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Debt balances"
        actions={
          totals.overdueCount > 0 ? (
            <Badge variant="warning">
              {totals.overdueCount} payment{totals.overdueCount === 1 ? '' : 's'} past due ·{' '}
              {formatCurrency(totals.overdueCents)}
            </Badge>
          ) : (
            <Badge variant="info">
              {rows.length} obligation{rows.length === 1 ? '' : 's'}
            </Badge>
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <MetricCard
          label="Total principal outstanding"
          value={formatCurrency(totals.balance)}
          loading={accountsLoading}
          hint={`Across ${rows.length} obligation${rows.length === 1 ? '' : 's'}`}
          icon={<Scale className="h-4 w-4 text-old-growth" strokeWidth={1.6} />}
        />
        <MetricCard
          label="Monthly debt service"
          value={formatCurrency(totals.monthly)}
          loading={accountsLoading}
          hint={
            totals.monthlyUnknown > 0
              ? `${totals.monthlyUnknown} credit card${totals.monthlyUnknown === 1 ? '' : 's'} vary by statement`
              : undefined
          }
          icon={<CalendarClock className="h-4 w-4 text-old-growth" strokeWidth={1.6} />}
        />
        <MetricCard
          label="Next payment due"
          value={totals.next ? formatISO(totals.next.nextDueDate) : '—'}
          loading={accountsLoading}
          hint={
            totals.next
              ? `${totals.next.name} · ${formatCurrency(totals.next.nextPaymentCents)}`
              : 'No scheduled payments'
          }
          icon={<HandCoins className="h-4 w-4 text-old-growth" strokeWidth={1.6} />}
        />
        <MetricCard
          label="Weighted avg. rate"
          value={formatRate(totals.avgRate)}
          loading={accountsLoading}
        />
        <MetricCard
          label="Interest still to pay"
          value={formatCurrency(totals.interest)}
          loading={accountsLoading}
        />
        <MetricCard
          label="Undrawn / available credit"
          value={formatCurrency(totals.available)}
          loading={accountsLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
            All obligations
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {accountsLoading ? (
            <div className="space-y-2 px-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Obligation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Original / limit</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Monthly</TableHead>
                  <TableHead>Next payment</TableHead>
                  <TableHead>Payoff</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10 pr-6" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <DebtTableRow key={r.id} row={r} />
                ))}
                <TableRow className="bg-secondary/40 font-medium">
                  <TableCell className="pl-6" colSpan={3}>
                    Total
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(totals.balance)}
                  </TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(totals.available)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(totals.monthly)}
                  </TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-5">
        <Card className="xl:col-span-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
              Upcoming payments
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            {upcoming.length === 0 ? (
              <div className="px-6">
                <EmptyState
                  icon={CalendarClock}
                  title="Nothing due"
                  className="py-10"
                />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">Due</TableHead>
                    <TableHead>Obligation</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Principal</TableHead>
                    <TableHead className="pr-6 text-right">Interest</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcoming.map((u) => (
                    <TableRow key={u.id} className={cn(u.overdue && 'bg-fresh-salmon/10')}>
                      <TableCell className="pl-6 tabular-nums">
                        <div className="flex flex-col">
                          <span className={cn(u.overdue && 'font-medium text-fresh-salmon-dark')}>
                            {formatISO(u.dueDate)}
                          </span>
                          {u.overdue && (
                            <span className="text-[10px] uppercase tracking-wide text-fresh-salmon-dark">
                              Past due
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{u.debtName}</span>
                          <span className="text-xs text-muted-foreground">{u.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(u.amountCents)}
                        {u.estimated && (
                          <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                            est.
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatCurrency(u.principalCents)}
                      </TableCell>
                      <TableCell className="pr-6 text-right tabular-nums text-muted-foreground">
                        {formatCurrency(u.interestCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-secondary/40 font-medium">
                    <TableCell className="pl-6" colSpan={2}>
                      Total due in window
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(upcoming.reduce((s, u) => s + u.amountCents, 0))}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(upcoming.reduce((s, u) => s + (u.principalCents ?? 0), 0))}
                    </TableCell>
                    <TableCell className="pr-6 text-right tabular-nums">
                      {formatCurrency(upcoming.reduce((s, u) => s + (u.interestCents ?? 0), 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <PayoffPlannerCard rows={rows} className="xl:col-span-2" />
      </div>

      <BookLiabilitiesCard items={bookLiabilities} loading={!coa || !monthlyBalances} />
    </div>
  );
}

function DebtTableRow({ row }: { row: DebtRow }) {
  const Icon = KIND_ICON[row.kind];
  return (
    <TableRow>
      <TableCell className="pl-6">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-secondary/40">
            <Icon className="h-3.5 w-3.5 text-old-growth" strokeWidth={1.6} />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="font-medium text-foreground">{row.name}</span>
            <span className="text-xs text-muted-foreground">
              {row.lender ?? '—'}
              <span className="mx-1.5 text-muted-foreground/50">·</span>
              {row.source}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">{row.kindLabel}</TableCell>
      <TableCell className="text-right tabular-nums">{formatRate(row.annualRate)}</TableCell>
      <TableCell className="text-right font-medium tabular-nums">
        {formatCurrency(row.balanceCents)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {formatCurrency(row.originalCents)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {formatCurrency(row.availableCents)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <div className="flex flex-col items-end">
          <span>{formatCurrency(row.monthlyPaymentCents)}</span>
          {row.monthlyPaymentHint && (
            <span className="max-w-[12rem] text-right text-[11px] leading-tight text-muted-foreground">
              {row.monthlyPaymentHint}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <div className="flex flex-col">
          <span className="tabular-nums">{formatISO(row.nextDueDate)}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {row.nextPaymentCents != null ? formatCurrency(row.nextPaymentCents) : row.nextPaymentHint}
            {row.nextPaymentCents != null && row.nextPaymentHint ? ` · ${row.nextPaymentHint}` : ''}
          </span>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <div className="flex flex-col">
          <span className="tabular-nums">{formatISO(row.payoffDate)}</span>
          {row.interestRemainingCents != null && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {formatCurrency(row.interestRemainingCents)} interest left
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={row.status.tone} className="whitespace-nowrap">
          {row.status.label}
        </Badge>
      </TableCell>
      <TableCell className="pr-6">
        {row.href && (
          <Link
            to={row.href}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-old-growth"
            aria-label={`Open ${row.name}`}
            title={`Open ${row.name}`}
          >
            <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
          </Link>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * "What if we paid extra?" — re-amortizes each loan with an additional
 * monthly principal payment so the operator can see how much interest
 * and time an extra $X/month buys on each note.
 */
function PayoffPlannerCard({ rows, className }: { rows: DebtRow[]; className?: string }) {
  const [extraInput, setExtraInput] = useState('500');
  const extraCents = Math.max(0, Math.round((Number.parseFloat(extraInput) || 0) * 100));

  const plans = rows
    .filter((r) => r.planner && r.annualRate != null && r.balanceCents > 0)
    .map((r) => {
      const planner = r.planner!;
      const baseline = simulatePayoff(r.balanceCents, r.annualRate!, planner.paymentCents, 0, planner);
      const accelerated = simulatePayoff(
        r.balanceCents,
        r.annualRate!,
        planner.paymentCents,
        extraCents,
        planner,
      );
      return { row: r, baseline, accelerated };
    });

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
          Payoff planner
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Extra principal per month
          </span>
          <div className="relative max-w-xs">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
              $
            </span>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step={50}
              value={extraInput}
              onChange={(e) => setExtraInput(e.target.value)}
              className="pl-7 tabular-nums"
            />
          </div>
        </label>

        {plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No amortizing loans to plan against.
          </p>
        ) : (
          <ul className="space-y-4">
            {plans.map(({ row, baseline, accelerated }) => {
              const monthsSaved =
                baseline && accelerated ? baseline.months - accelerated.months : null;
              const interestSaved =
                baseline && accelerated
                  ? baseline.totalInterestCents - accelerated.totalInterestCents
                  : null;
              return (
                <li key={row.id} className="rounded-lg border border-border bg-card/60 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{row.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(row.balanceCents)} at {formatRate(row.annualRate)} ·{' '}
                        {formatCurrency(row.planner!.paymentCents)}/mo
                        {row.kind === 'sba-loan' ? ' once amortizing' : ''}
                      </p>
                    </div>
                    {interestSaved != null && interestSaved > 0 && (
                      <Badge variant="success" className="whitespace-nowrap">
                        Save {formatCurrency(interestSaved, { whole: true })}
                      </Badge>
                    )}
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                    <PlanStat
                      label="Payoff today"
                      value={baseline ? formatISO(baseline.payoffDate) : '—'}
                      hint={baseline ? `${baseline.months} payments` : undefined}
                    />
                    <PlanStat
                      label="Payoff with extra"
                      value={accelerated ? formatISO(accelerated.payoffDate) : 'Never'}
                      hint={
                        accelerated
                          ? `${accelerated.months} payments${
                              monthsSaved != null && monthsSaved > 0 ? ` · ${monthsSaved} fewer` : ''
                            }`
                          : 'Payment does not cover interest'
                      }
                    />
                    <PlanStat
                      label="Interest today"
                      value={baseline ? formatCurrency(baseline.totalInterestCents) : '—'}
                    />
                    <PlanStat
                      label="Interest with extra"
                      value={accelerated ? formatCurrency(accelerated.totalInterestCents) : '—'}
                      hint={
                        interestSaved != null && interestSaved > 0
                          ? `${formatCurrency(interestSaved)} saved`
                          : undefined
                      }
                      emphasis={interestSaved != null && interestSaved > 0}
                    />
                  </dl>
                </li>
              );
            })}
          </ul>
        )}

      </CardContent>
    </Card>
  );
}

function PlanStat({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          'font-medium tabular-nums tracking-headline',
          emphasis ? 'text-bright-moss-dark' : 'text-foreground',
        )}
      >
        {value}
      </dd>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

interface BookLiability {
  id: string;
  code: string | null;
  name: string;
  subtype: string | null;
  bucket: 'Credit card' | 'Long-term liability';
  balanceCents: number;
  asOf: string;
}

/**
 * Liability accounts on the QuickBooks balance sheet that look like
 * debt (credit cards, notes payable, long-term liabilities). Accounts
 * already mapped to a synced bank/credit account are dropped so the
 * Mercury card doesn't show up twice. AP, payroll, and sales-tax
 * liabilities are intentionally excluded — they're obligations, not
 * borrowed money.
 */
function buildBookLiabilities(
  coa: Tables<'chart_of_accounts'>[],
  balances: { account_id: string; month: string; balance_cents: number }[],
  accounts: Tables<'bank_accounts'>[],
): BookLiability[] {
  if (coa.length === 0 || balances.length === 0) return [];

  const mapped = new Set(accounts.map((a) => a.qb_account_id).filter(Boolean));
  const latest = new Map<string, { month: string; balance_cents: number }>();
  for (const b of balances) {
    const cur = latest.get(b.account_id);
    if (!cur || b.month > cur.month) latest.set(b.account_id, b);
  }

  const out: BookLiability[] = [];
  for (const a of coa) {
    if (!a.is_active || a.account_type !== 'liability') continue;
    if (mapped.has(a.id)) continue;
    const sub = (a.subtype ?? '').toLowerCase().replace(/[\s_-]/g, '');
    let bucket: BookLiability['bucket'] | null = null;
    if (sub === 'creditcard') bucket = 'Credit card';
    else if (sub.includes('longterm') || sub.includes('notespayable') || sub.includes('mortgage')) {
      bucket = 'Long-term liability';
    }
    if (!bucket) continue;
    const bal = latest.get(a.id);
    if (!bal || bal.balance_cents === 0) continue;
    out.push({
      id: a.id,
      code: a.code,
      name: a.name,
      subtype: a.subtype,
      bucket,
      balanceCents: bal.balance_cents,
      asOf: bal.month,
    });
  }
  return out.sort((x, y) => y.balanceCents - x.balanceCents);
}

function BookLiabilitiesCard({ items, loading }: { items: BookLiability[]; loading: boolean }) {
  const total = items.reduce((s, i) => s + i.balanceCents, 0);
  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
            Debt on the books (QuickBooks)
          </CardTitle>
        </div>
        {!loading && items.length > 0 && (
          <Badge variant="muted" className="whitespace-nowrap">
            {formatCurrency(total)} booked
          </Badge>
        )}
      </CardHeader>
      <CardContent className="px-0">
        {loading ? (
          <div className="space-y-2 px-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="px-6">
            <EmptyState
              icon={BookOpen}
              title="No debt accounts on the balance sheet"
              className="py-10"
            />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Account</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>As of</TableHead>
                <TableHead className="pr-6 text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="pl-6">
                    <span className="font-medium">
                      {i.code ? `${i.code} · ` : ''}
                      {i.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{i.bucket}</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">
                    {formatMonth(i.asOf)}
                  </TableCell>
                  <TableCell className="pr-6 text-right tabular-nums">
                    {formatCurrency(i.balanceCents)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

const MONTH_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
function formatMonth(iso: string): string {
  // `month` comes back as 'YYYY-MM-01' (or 'YYYY-MM'); parse the parts
  // directly to avoid the UTC-midnight day shift.
  const [y, m] = iso.split('-').map((s) => Number.parseInt(s, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return iso;
  return MONTH_FMT.format(new Date(y, m - 1, 1));
}
